import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp } from 'lucide-react';
import { STORY_PANELS } from '../constants';
import { ParentsInBoat, ParentsTogether } from './svg/Characters';
import { RIBBoat } from './svg/BoatSVG';

const ScrollytellingIntro = ({ onComplete }) => {
  const [currentPanel, setCurrentPanel] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const containerRef = useRef(null);

  const panels = STORY_PANELS;
  const isLastPanel = currentPanel === panels.length - 1;

  // Handle swipe/scroll
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientY);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    
    const touchEnd = e.changedTouches[0].clientY;
    const diff = touchStart - touchEnd;
    
    if (diff > 50 && currentPanel < panels.length - 1) {
      // Swipe up - next panel
      setCurrentPanel(prev => prev + 1);
    } else if (diff < -50 && currentPanel > 0) {
      // Swipe down - previous panel
      setCurrentPanel(prev => prev - 1);
    }
    
    setTouchStart(null);
  };

  // Handle wheel scroll
  const handleWheel = (e) => {
    if (e.deltaY > 30 && currentPanel < panels.length - 1) {
      setCurrentPanel(prev => prev + 1);
    } else if (e.deltaY < -30 && currentPanel > 0) {
      setCurrentPanel(prev => prev - 1);
    }
  };

  // Handle keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        if (currentPanel < panels.length - 1) {
          setCurrentPanel(prev => prev + 1);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentPanel > 0) {
          setCurrentPanel(prev => prev - 1);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPanel, panels.length]);

  const panel = panels[currentPanel];

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden no-select"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      data-testid="scrollytelling-container"
    >
      {/* Progress dots */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {panels.map((_, idx) => (
          <motion.div
            key={idx}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              idx === currentPanel ? 'bg-sky-500 scale-125' : 
              idx < currentPanel ? 'bg-yellow-400' : 'bg-white/50'
            }`}
            data-testid={`progress-dot-${idx}`}
          />
        ))}
      </div>

      {/* Panel content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentPanel}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col items-center justify-center px-6 text-center z-10"
        >
          {/* Visual elements based on panel */}
          <div className="mb-4 min-h-[280px] flex items-center justify-center">
            {panel.showCharacters && !panel.showBoat && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <ParentsTogether size="lg" />
              </motion.div>
            )}
            
            {panel.showBoat && panel.showCharacters && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <ParentsInBoat size="large" />
              </motion.div>
            )}
            
            {panel.showBoat && !panel.showCharacters && (
              <motion.div
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
              >
                <RIBBoat size="lg" />
              </motion.div>
            )}
            
            {panel.fakeOut && (
              <motion.div
                initial={{ scale: 2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-6xl"
              >
                🎁
              </motion.div>
            )}
            
            {panel.isFinal && (
              <motion.div
                initial={{ rotate: -10, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="text-7xl"
              >
                🚀
              </motion.div>
            )}
            
            {!panel.showCharacters && !panel.showBoat && !panel.fakeOut && !panel.isFinal && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
                className="text-6xl"
              >
                👋
              </motion.div>
            )}
          </div>

          {/* Speech bubble for text */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-3xl px-8 py-6 max-w-xs shadow-lg"
          >
            <h2 
              className="text-3xl font-black text-slate-900 mb-2"
              style={{ fontFamily: 'Nunito, sans-serif' }}
              data-testid={`story-text-${currentPanel}`}
            >
              {panel.text}
            </h2>
            <p 
              className="text-lg text-slate-600"
              style={{ fontFamily: 'Quicksand, sans-serif' }}
            >
              {panel.subtext}
            </p>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Swipe indicator or Continue button */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20">
        {isLastPanel ? (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onComplete}
            className="btn-sun flex items-center gap-2 animate-pulse-glow"
            data-testid="start-game-button"
          >
            Los geht's!
            <ChevronUp className="w-5 h-5" />
          </motion.button>
        ) : (
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="flex flex-col items-center text-slate-500"
          >
            <ChevronUp className="w-8 h-8" />
            <span className="text-sm mt-1">Wisch hoch</span>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ScrollytellingIntro;
